import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import CmsElementRenderer from '../cms/CmsElementRenderer';
import Icon from '../common/Icon';
import { icons } from '../../config/icons';
import { useProject } from '../../context/ProjectContext';

const Navbar = ({ settings, settingsTimestamp }) => {
    const { projectId, isProjectLoading } = useProject();
    const [headerData, setHeaderData] = useState(null);
    const [navigations, setNavigations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!document.contains(e.target)) return;
            if (
                openDropdownId !== null && 
                !e.target.closest('.web-navbar-item') &&
                !e.target.closest('.cms-props') &&
                !e.target.closest('.cms-sidebar') &&
                !e.target.closest('.cms-header') &&
                !e.target.closest('[class*="toolbar"]') &&
                !e.target.closest('.popup-menu-container') &&
                !e.target.closest('.popup-toolbar-btn')
            ) {
                setOpenDropdownId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openDropdownId]);

    // Re-render on window resize so responsive nav settings update without a page refresh
    useEffect(() => {
        const onResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (isProjectLoading) return;

        const cacheKeyHeader = `cms_header_${projectId || 'default'}`;
        const cacheKeyNav = `cms_nav_${projectId || 'default'}`;
        
        // Stale-While-Revalidate: Load from cache first
        const cachedHeader = localStorage.getItem(cacheKeyHeader);
        const cachedNav = localStorage.getItem(cacheKeyNav);
        
        if (cachedHeader) {
            try { setHeaderData(JSON.parse(cachedHeader)); } catch (e) {}
        }
        if (cachedNav) {
            try { setNavigations(JSON.parse(cachedNav)); } catch (e) {}
        }
        
        // If we don't have cache, show loading
        if (!cachedHeader || !cachedNav) {
            setLoading(true);
        }

        const fetchHeaderAndNav = async () => {
            try {
                const [headerRes, navRes] = await Promise.all([
                    api.get('/api/web/header', { params: { id_project: projectId || '', _t: settingsTimestamp } }),
                    api.get('/api/web/navigations', { params: { id_project: projectId || '', _t: settingsTimestamp } })
                ]);

                if (headerRes.data.success) {
                    setHeaderData(headerRes.data.data);
                    localStorage.setItem(cacheKeyHeader, JSON.stringify(headerRes.data.data));
                }
                if (navRes.data.success) {
                    setNavigations(navRes.data.data);
                    localStorage.setItem(cacheKeyNav, JSON.stringify(navRes.data.data));
                }
            } catch (error) {
                console.error('Failed to fetch public header/navigations:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchHeaderAndNav();
    }, [settingsTimestamp, projectId, isProjectLoading]);

    if (!loading && headerData) {
        const s = headerData.settings || {};
        const t = s.responsive?.tablet || {};
        const m = s.responsive?.mobile || {};
        const elements = headerData.elements || [];
        const style = {};

        const setVar = (varName, value) => {
            if (value !== undefined && value !== null && value !== '') {
                style[varName] = value;
            }
        };

        // Map header settings to CSS variables matching CmsSectionRenderer
        setVar('--sec-bg-color', s.bgColor);
        if (s.bgImage) style['--sec-bg-image'] = `url(${s.bgImage})`;
        setVar('--sec-padding', s.padding);
        setVar('--sec-margin', s.margin);
        setVar('--sec-color', s.color);
        setVar('--sec-font-size', s.fontSize);
        setVar('--sec-font-weight', s.fontWeight);
        if (s.boxShadow) style.boxShadow = s.boxShadow;

        // Background advanced settings
        setVar('--sec-bg-position', s.bgPosition);
        setVar('--sec-bg-repeat', s.bgRepeat);
        setVar('--sec-bg-size', s.bgSize);

        // Overlay
        setVar('--sec-overlay-color', s.overlayColor);
        if (s.overlayOpacity !== undefined && s.overlayOpacity !== '') style['--sec-overlay-opacity'] = s.overlayOpacity;

        // Border
        setVar('--sec-border-width', s.borderWidth);
        setVar('--sec-border-style', s.borderStyle);
        setVar('--sec-border-color', s.borderColor);
        setVar('--sec-border-radius', s.borderRadius);

        // Output viewport-specific responsive variables for the CSS cascade
        setVar('--s-tablet-bg-color', t.bgColor);
        setVar('--s-tablet-padding', t.padding);
        setVar('--s-tablet-margin', t.margin);
        setVar('--s-tablet-font-size', t.fontSize);
        setVar('--s-tablet-font-weight', t.fontWeight);
        setVar('--s-tablet-color', t.color);
        setVar('--s-tablet-border-width', t.borderWidth);
        setVar('--s-tablet-border-color', t.borderColor);
        setVar('--s-tablet-border-radius', t.borderRadius);
        
        setVar('--s-mobile-bg-color', m.bgColor);
        setVar('--s-mobile-padding', m.padding);
        setVar('--s-mobile-margin', m.margin);
        setVar('--s-mobile-font-size', m.fontSize);
        setVar('--s-mobile-font-weight', m.fontWeight);
        setVar('--s-mobile-color', m.color);
        setVar('--s-mobile-border-width', m.borderWidth);
        setVar('--s-mobile-border-color', m.borderColor);
        setVar('--s-mobile-border-radius', m.borderRadius);

        // Header positioning (sticky, fixed, absolute, static)
        if (s.position) style.position = s.position;
        if (s.top !== undefined && s.top !== '') style.top = s.top;
        if (s.zIndex) style.zIndex = s.zIndex;

        const navSettings = s.navSettings || {};
        const navT = t.navSettings || {};
        const navM = m.navSettings || {};
        const mobileNavStyle = s.mobileNavStyle || navSettings.mobileNavStyle || 'popup';
        if (navSettings.gap) style['--nav-gap'] = navSettings.gap;
        if (navSettings.color) style['--nav-color'] = navSettings.color;
        if (navSettings.hoverColor) style['--nav-hover-color'] = navSettings.hoverColor;
        if (navSettings.fontSize) style['--nav-font-size'] = navSettings.fontSize;
        if (navSettings.fontWeight) style['--nav-font-weight'] = navSettings.fontWeight;
        if (navSettings.hoverBgColor) style['--nav-hover-bg'] = navSettings.hoverBgColor;
        if (navSettings.borderRadius) style['--nav-border-radius'] = navSettings.borderRadius;
        if (navSettings.transition) style['--nav-transition'] = navSettings.transition;

        // Tablet nav overrides (picked up by @media queries)
        if (navT.gap) style['--nav-tablet-gap'] = navT.gap;
        if (navT.color) style['--nav-tablet-color'] = navT.color;
        if (navT.fontSize) style['--nav-tablet-font-size'] = navT.fontSize;
        if (navT.fontWeight) style['--nav-tablet-font-weight'] = navT.fontWeight;
        if (navT.borderRadius) style['--nav-tablet-border-radius'] = navT.borderRadius;
        if (navT.width) style['--nav-tablet-width'] = navT.width;

        // Mobile nav overrides
        if (navM.gap) style['--nav-mobile-gap'] = navM.gap;
        if (navM.color) style['--nav-mobile-color'] = navM.color;
        if (navM.fontSize) style['--nav-mobile-font-size'] = navM.fontSize;
        if (navM.fontWeight) style['--nav-mobile-font-weight'] = navM.fontWeight;
        if (navM.borderRadius) style['--nav-mobile-border-radius'] = navM.borderRadius;
        if (navM.width) style['--nav-mobile-width'] = navM.width;

        const logoStyle = {};
        if (s.logoSettings?.height) logoStyle.height = s.logoSettings.height;
        if (s.logoSettings?.width) logoStyle.width = s.logoSettings.width;

        return (
            <div className={`seczone headzone cms-sec-header ${menuOpen ? 'menu-open' : ''} mobile-nav-${mobileNavStyle}`} style={style}>
                <div className="seczone-in">
                    <header className={`web-header ${s.innerClass || ''}`} style={{
                        flexDirection: s.flexDirection,
                        alignItems: s.alignItems,
                        justifyContent: s.justifyContent,
                        height: s.height
                    }}>
                        {s.logoLinkToHome ? (
                            <Link 
                                to="/" 
                                className='web-header-left'
                                onClick={(e) => {
                                    if (windowWidth <= 767) {
                                        setMenuOpen(false);
                                    }
                                }}
                            >
                                <div className='web-header-logo' style={logoStyle}>
                                    <img src={s.logoSettings?.src || (settings?.logo ? `/defaults/logo/${settings.logo}?t=${settingsTimestamp}` : "/defaults/dashboard/dashboard.png")} alt="logo" />
                                </div>
                                {(() => {
                                    const showTitle = s.showTitle ?? true;
                                    const showSubtitle = s.showSubtitle ?? true;

                                    const mappedContent = (elements || []).map((b, i) => ({ ...b, _pathIdx: i }));
                                    const textElements = mappedContent.filter(b => b.element_type !== 'button');

                                    let titleElement = textElements.find(b => b.tag === 'h1');
                                    let subtitleElement = textElements.find(b => b.tag === 'h4');

                                    if (!titleElement && textElements.length > 0) {
                                        const firstElement = textElements.find(b => b._pathIdx !== subtitleElement?._pathIdx);
                                        if (firstElement) titleElement = { ...firstElement, tag: 'h1' };
                                    }
                                    if (!subtitleElement && textElements.length > 1) {
                                        const secondElement = textElements.find(b => b._pathIdx !== titleElement?._pathIdx);
                                        if (secondElement) subtitleElement = { ...secondElement, tag: 'h4' };
                                    }

                                    const hasTitleContent = showTitle && titleElement;
                                    const hasSubtitleContent = showSubtitle && subtitleElement;

                                    if (!hasTitleContent && !hasSubtitleContent) return null;

                                    return (
                                        <div className='web-header-titles'>
                                            {hasTitleContent && <CmsElementRenderer element={titleElement} sectionId="header" parentPath={`${titleElement._pathIdx}`} />}
                                            {hasSubtitleContent && <CmsElementRenderer element={subtitleElement} sectionId="header" parentPath={`${subtitleElement._pathIdx}`} />}
                                        </div>
                                    );
                                })()}
                            </Link>
                        ) : (
                            <div 
                                className='web-header-left'
                                onClick={() => {
                                    if (windowWidth <= 767) {
                                        setMenuOpen(false);
                                    }
                                }}
                            >
                                <div className='web-header-logo' style={logoStyle}>
                                    <img src={s.logoSettings?.src || (settings?.logo ? `/defaults/logo/${settings.logo}?t=${settingsTimestamp}` : "/defaults/dashboard/dashboard.png")} alt="logo" />
                                </div>
                                {(() => {
                                    const showTitle = s.showTitle ?? true;
                                    const showSubtitle = s.showSubtitle ?? true;

                                    const mappedContent = (elements || []).map((b, i) => ({ ...b, _pathIdx: i }));
                                    const textElements = mappedContent.filter(b => b.element_type !== 'button');

                                    let titleElement = textElements.find(b => b.tag === 'h1');
                                    let subtitleElement = textElements.find(b => b.tag === 'h4');

                                    if (!titleElement && textElements.length > 0) {
                                        const firstElement = textElements.find(b => b._pathIdx !== subtitleElement?._pathIdx);
                                        if (firstElement) titleElement = { ...firstElement, tag: 'h1' };
                                    }
                                    if (!subtitleElement && textElements.length > 1) {
                                        const secondElement = textElements.find(b => b._pathIdx !== titleElement?._pathIdx);
                                        if (secondElement) subtitleElement = { ...secondElement, tag: 'h4' };
                                    }

                                    const hasTitleContent = showTitle && titleElement;
                                    const hasSubtitleContent = showSubtitle && subtitleElement;

                                    if (!hasTitleContent && !hasSubtitleContent) return null;

                                    return (
                                        <div className='web-header-titles'>
                                            {hasTitleContent && <CmsElementRenderer element={titleElement} sectionId="header" parentPath={`${titleElement._pathIdx}`} />}
                                            {hasSubtitleContent && <CmsElementRenderer element={subtitleElement} sectionId="header" parentPath={`${subtitleElement._pathIdx}`} />}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {(() => {
                            // Strict per-project rule: no navigations → no nav UI at all
                            if (!navigations || navigations.length === 0) return null;
                            // Merge base navSettings with viewport overrides
                            const w = windowWidth;
                            const navT = t.navSettings || {};
                            const navM = m.navSettings || {};
                            const navSettings = w <= 767 ? { ...s.navSettings, ...navT, ...navM } : 
                                               w <= 1024 ? { ...s.navSettings, ...navT } : 
                                               (s.navSettings || {});
                            const navStyle = {};
                            if (navSettings.gap) navStyle['--nav-gap'] = navSettings.gap;
                            if (navSettings.color) navStyle['--nav-color'] = navSettings.color;
                            if (navSettings.hoverColor) navStyle['--nav-hover-color'] = navSettings.hoverColor;
                            if (navSettings.fontSize) navStyle['--nav-font-size'] = navSettings.fontSize;
                            if (navSettings.hoverFontSize) navStyle['--nav-hover-font-size'] = navSettings.hoverFontSize;
                            if (navSettings.fontWeight) navStyle['--nav-font-weight'] = navSettings.fontWeight;
                            if (navSettings.hoverFontWeight) navStyle['--nav-hover-font-weight'] = navSettings.hoverFontWeight;
                            if (navSettings.fontStyle) navStyle['--nav-font-style'] = navSettings.fontStyle;
                            if (navSettings.hoverFontStyle) navStyle['--nav-hover-font-style'] = navSettings.hoverFontStyle;
                            if (navSettings.textDecoration) navStyle['--nav-text-decoration'] = navSettings.textDecoration;
                            if (navSettings.hoverTextDecoration) navStyle['--nav-hover-text-decoration'] = navSettings.hoverTextDecoration;
                            if (navSettings.padding) {
                                navStyle['--nav-container-padding'] = navSettings.padding;
                                navStyle.padding = navSettings.padding;
                            }
                            if (navSettings.margin) {
                                navStyle.margin = navSettings.margin;
                            }
                            if (navSettings.bgColor) {
                                navStyle['--nav-container-bg'] = navSettings.bgColor;
                                navStyle.backgroundColor = navSettings.bgColor;
                            }
                            if (navSettings.hoverBgColor) navStyle['--nav-hover-bg'] = navSettings.hoverBgColor;
                            if (navSettings.borderRadius) navStyle['--nav-border-radius'] = navSettings.borderRadius;
                            if (navSettings.borderWidth) navStyle['--nav-border-width'] = navSettings.borderWidth;
                            if (navSettings.borderStyle) navStyle['--nav-border-style'] = navSettings.borderStyle;
                            if (navSettings.borderColor) navStyle['--nav-border-color'] = navSettings.borderColor;
                            if (navSettings.boxShadow) navStyle['--nav-box-shadow'] = navSettings.boxShadow;
                            if (navSettings.transition) navStyle['--nav-transition'] = navSettings.transition;
                            if (navSettings.height) navStyle.height = navSettings.height;
                            if (navSettings.width) navStyle.width = navSettings.width;
                            if (navSettings.flexDirection) navStyle.flexDirection = navSettings.flexDirection;
                            navStyle.justifyContent = navSettings.justifyContent || (s.navAlign === 'left' ? 'flex-start' : s.navAlign === 'right' ? 'flex-end' : 'center');
                            if (navSettings.alignItems) navStyle.alignItems = navSettings.alignItems;

                            const dropdownSettings = s.dropdownSettings || {};
                            const dropdownStyle = {};
                            if (dropdownSettings.bgColor) dropdownStyle['--dropdown-bg'] = dropdownSettings.bgColor;
                            if (dropdownSettings.padding) dropdownStyle['--dropdown-padding'] = dropdownSettings.padding;
                            if (dropdownSettings.borderRadius) dropdownStyle['--dropdown-border-radius'] = dropdownSettings.borderRadius;
                            if (dropdownSettings.boxShadow) dropdownStyle['--dropdown-box-shadow'] = dropdownSettings.boxShadow;
                            if (dropdownSettings.borderWidth) dropdownStyle['--dropdown-border-width'] = dropdownSettings.borderWidth;
                            if (dropdownSettings.borderColor) dropdownStyle['--dropdown-border-color'] = dropdownSettings.borderColor;
                            
                            if (dropdownSettings.color) dropdownStyle['--dropdown-link-color'] = dropdownSettings.color;
                            if (dropdownSettings.hoverColor) dropdownStyle['--dropdown-link-hover-color'] = dropdownSettings.hoverColor;
                            if (dropdownSettings.hoverBgColor) dropdownStyle['--dropdown-link-hover-bg'] = dropdownSettings.hoverBgColor;
                            if (dropdownSettings.fontSize) dropdownStyle['--dropdown-link-font-size'] = dropdownSettings.fontSize;
                            if (dropdownSettings.fontWeight) dropdownStyle['--dropdown-link-font-weight'] = dropdownSettings.fontWeight;
                            if (dropdownSettings.padding) dropdownStyle['--dropdown-link-padding'] = dropdownSettings.padding;

                            const getLocalNavStyle = (nav, isDropdown) => {
                                if (!nav) return {};
                                const settings = nav.settings || {};
                                const tablet = nav.responsive?.tablet?.settings || {};
                                const mobile = nav.responsive?.mobile?.settings || {};
                                
                                const s = {};
                                const prefix = isDropdown ? '--dropdown-link-' : '--nav-';
                                
                                const setResponsiveVars = (propKey, cssVarPart) => {
                                    if (settings[propKey]) s[`${prefix}${cssVarPart}`] = settings[propKey];
                                    if (tablet[propKey]) s[`${prefix}tablet-${cssVarPart}`] = tablet[propKey];
                                    if (mobile[propKey]) s[`${prefix}mobile-${cssVarPart}`] = mobile[propKey];
                                };

                                setResponsiveVars('color', 'color');
                                setResponsiveVars('hover-color', 'hover-color');
                                setResponsiveVars('bg-color', 'bg');
                                setResponsiveVars('hover-bg-color', 'hover-bg');
                                setResponsiveVars('font-size', 'font-size');
                                setResponsiveVars('font-weight', 'font-weight');
                                setResponsiveVars('padding', 'padding');
                                setResponsiveVars('margin', 'margin');
                                setResponsiveVars('border-radius', 'border-radius');
                                setResponsiveVars('width', 'width');
                                setResponsiveVars('height', 'height');

                                if (settings.transition) {
                                    s[`${prefix}transition`] = settings.transition;
                                    s['--b-transition'] = settings.transition;
                                }
                                return s;
                            };

                            const getResponsiveClasses = (nav, isDropdown) => {
                                if (!nav) return '';
                                const s = nav.settings || {};
                                const t = nav.responsive?.tablet?.settings || {};
                                const m = nav.responsive?.mobile?.settings || {};
                                const baseClass = isDropdown ? 'web-dropdown-link' : 'web-navbar-link';
                                
                                return [
                                    baseClass,
                                    s['button-style'] || '',
                                    s['button-variant'] || '',
                                    s['button-size'] || '',
                                    (s['button-style'] || t['button-style'] || m['button-style'] || s['button-variant'] || t['button-variant'] || m['button-variant']) ? 'button' : '',
                                    t['button-style'] ? `tablet-${t['button-style']}` : '',
                                    t['button-variant'] ? `tablet-${t['button-variant']}` : '',
                                    t['button-size'] ? `tablet-${t['button-size']}` : '',
                                    m['button-style'] ? `mobile-${m['button-style']}` : '',
                                    m['button-variant'] ? `mobile-${m['button-variant']}` : '',
                                    m['button-size'] ? `mobile-${m['button-size']}` : '',
                                    s.hoverScale === 'zoom' ? 'hover-zoom' : '',
                                    s.hideDesktop ? 'd-none-desktop' : '',
                                    s.hideTablet ? 'd-none-tablet' : '',
                                    s.hideMobile ? 'd-none-mobile' : ''
                                ].filter(Boolean).join(' ');
                            };

                            const navVisibilityClass = [
                                navSettings.hideDesktop ? 'd-none-desktop' : '',
                                navSettings.hideTablet ? 'd-none-tablet' : '',
                                navSettings.hideMobile ? 'd-none-mobile' : ''
                            ].filter(Boolean).join(' ');

                            return (
                                <div className={`web-header-middle ${navVisibilityClass}`.trim()} style={navStyle}>
                                    <button className="mobile-nav-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                                        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                    </button>
                                    {navigations && navigations.filter(nav => !nav.parent_id).map(parentNav => {
                                        const subLinks = navigations.filter(nav => nav.parent_id === parentNav.id);
                                        const hasSubLinks = subLinks.length > 0;
                                        const dropdownTrigger = headerData?.settings?.dropdownTrigger || 'hover';
                                        const isOpen = openDropdownId === parentNav.id;

                                        return (
                                            <div 
                                                key={parentNav.id} 
                                                className={`web-navbar-item ${hasSubLinks ? 'has-dropdown' : ''} ${dropdownTrigger === 'hover' ? 'trigger-hover' : 'trigger-click'} ${isOpen ? 'is-open' : ''} ${parentNav.settings?.hideDesktop ? 'd-none-desktop' : ''} ${parentNav.settings?.hideTablet ? 'd-none-tablet' : ''} ${parentNav.settings?.hideMobile ? 'd-none-mobile' : ''}`.trim()}
                                            >
                                                <Link 
                                                    className={getResponsiveClasses(parentNav, false)}
                                                    to={parentNav.url || '#'}
                                                    target={parentNav.settings?.target || '_self'}
                                                    title={parentNav.settings?.title || ''}
                                                    aria-label={parentNav.settings?.['aria-label'] || ''}
                                                    draggable="false"
                                                    style={getLocalNavStyle(parentNav, false)}
                                                    onClick={(e) => {
                                                        const isMobile = window.innerWidth <= 767;
                                                        if (hasSubLinks && (dropdownTrigger === 'click' || isMobile)) {
                                                            e.preventDefault();
                                                            setOpenDropdownId(isOpen ? null : parentNav.id);
                                                        } else if (!hasSubLinks && isMobile) {
                                                            setMenuOpen(false);
                                                        }
                                                    }}
                                                >
                                                    {parentNav.settings?.iconLeft && <Icon icon={icons[parentNav.settings.iconLeft]} style={{ marginRight: parentNav.settings.iconGap || '0px' }} />}
                                                    {parentNav.label}
                                                    {parentNav.settings?.iconRight && <Icon icon={icons[parentNav.settings.iconRight]} style={{ marginLeft: parentNav.settings.iconGap || '0px' }} />}
                                                    {hasSubLinks && <Icon icon={icons.chevronDown} className="dropdown-icon" />}
                                                </Link>
                                                {hasSubLinks && (
                                                    <div className='web-navbar-dropdown' style={dropdownStyle}>
                                                        {subLinks.map(subNav => (
                                                            <Link 
                                                                key={subNav.id} 
                                                                className={getResponsiveClasses(subNav, true)}
                                                                to={subNav.url || '#'}
                                                                target={subNav.settings?.target || '_self'}
                                                                title={subNav.settings?.title || ''}
                                                                aria-label={subNav.settings?.['aria-label'] || ''}
                                                                draggable="false"
                                                                style={getLocalNavStyle(subNav, true)}
                                                                onClick={() => {
                                                                    if (window.innerWidth <= 767) setMenuOpen(false);
                                                                }}
                                                            >
                                                                {subNav.settings?.iconLeft && <Icon icon={icons[subNav.settings.iconLeft]} style={{ marginRight: subNav.settings.iconGap || '0px' }} />}
                                                                {subNav.label}
                                                                {subNav.settings?.iconRight && <Icon icon={icons[subNav.settings.iconRight]} style={{ marginLeft: subNav.settings.iconGap || '0px' }} />}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        <div className='web-header-right'>
                            {s.showCustomButtons !== false && (elements || [])
                                .map((b, i) => ({ ...b, _pathIdx: i }))
                                .filter(b => b.element_type === 'button')
                                .map((element) => (
                                    <CmsElementRenderer key={element._pathIdx} element={element} sectionId="header" parentPath={`${element._pathIdx}`} />
                                ))
                            }
                            {navigations && navigations.length > 0 && (
                                <button className="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
                                    <span></span>
                                </button>
                            )}
                        </div>
                    </header>
                    {mobileNavStyle === 'drawer' && (
                        <div className={`mobile-nav-backdrop ${menuOpen ? 'active' : ''}`} onClick={() => setMenuOpen(false)}></div>
                    )}
                </div>
            </div>
        );
    }

    if (loading) {
        return <header className='header' style={{ opacity: 0, minHeight: '80px' }}></header>;
    }

    // Fallback: If both cache and API fail (no data), show minimal skeleton instead of confusing template
    return <header className='header' style={{ opacity: 0, minHeight: '80px' }}></header>;
};

export default Navbar;
